<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import AppLayout from '@/layouts/AppLayout.vue'
import TicketDialog from '@/components/tickets/TicketDialog.vue'
import { listServiceTickets, updateServiceTicketStatus } from '@/services/tickets'
import { useAuth } from '@/composables/useAuth'
import type { ServiceTicket, TicketStatus } from '@/types/tickets'

const tickets = ref<ServiceTicket[]>([])
const loading = ref(true)
const error = ref('')
const createOpen = ref(false)
const updating = ref('')
const { role } = useAuth()
const canManage = computed(() => role.value === 'admin')

async function load() {
  loading.value = true
  error.value = ''
  try { tickets.value = await listServiceTickets() } catch { error.value = 'Unable to load service tickets.' } finally { loading.value = false }
}
async function setStatus(ticket: ServiceTicket, status: TicketStatus) {
  updating.value = ticket.id
  try { const updated = await updateServiceTicketStatus(ticket.id, status); tickets.value = tickets.value.map(item => item.id === updated.id ? updated : item) } catch { error.value = 'Unable to update this ticket.' } finally { updating.value = '' }
}
const date = (value?: string) => value ? new Date(value).toLocaleString() : 'Just now'
onMounted(load)
</script>

<template>
  <AppLayout>
    <header class="page-heading ticket-page-heading"><div><p class="eyebrow">SERVICE IMPROVEMENT</p><h1>Issues & reviews</h1><p>Shared follow-up from prep, services, and post-service reviews.</p></div><button class="button primary" @click="createOpen = true">Report an issue</button></header>
    <p v-if="error" class="error">{{ error }}</p>
    <p v-else-if="loading" class="muted">Loading tickets…</p>
    <section v-else class="ticket-list"><article v-for="ticket in tickets" :key="ticket.id" class="panel ticket-card" :class="`severity-${ticket.severity}`"><header><div><span class="ticket-type">{{ ticket.type === 'service-review' ? 'Service review' : 'Issue' }}</span><h2>{{ ticket.title }}</h2><p>{{ ticket.serviceTitle || 'General AV operations' }}</p></div><span class="ticket-status" :class="ticket.status">{{ ticket.status }}</span></header><p v-if="ticket.details">{{ ticket.details }}</p><dl v-if="ticket.mistakes || ticket.whatWentWell"><template v-if="ticket.mistakes"><dt>Process gaps</dt><dd>{{ ticket.mistakes }}</dd></template><template v-if="ticket.whatWentWell"><dt>What went well</dt><dd>{{ ticket.whatWentWell }}</dd></template></dl><footer><small>{{ ticket.severity }} · {{ ticket.createdByName || ticket.createdByEmail || 'Portal user' }} · {{ date(ticket.createdAt) }}</small><select v-if="canManage" :value="ticket.status" :disabled="updating === ticket.id" @change="setStatus(ticket, ($event.target as HTMLSelectElement).value as TicketStatus)"><option value="open">Open</option><option value="acknowledged">Acknowledged</option><option value="resolved">Resolved</option></select></footer></article><p v-if="!tickets.length" class="muted panel">No tickets or service reviews have been submitted yet.</p></section>
    <TicketDialog :open="createOpen" @close="createOpen = false" @created="load" />
  </AppLayout>
</template>
